import React, { useCallback, useState, useMemo } from "react";
// Mightier Survey Engine
import {
  BooleanQuestion,
  BooleanResponse,
} from "@neuromotion-inc/mightier-survey-engine";
import { SurveyEngineIface } from "src/services/surveyHooks/useSurveyEngine";
// Styling
import { Container, Text, Stack } from "@mantine/core";
import { OptionButton } from "../options-button/options-button";

interface SurveyBooleanInputProps {
  question: BooleanQuestion;
  surveyEngine: SurveyEngineIface;
  questionIdToValidationErrorsMap: Map<string, string[]>;
  testId: string;
}

export const SurveyBooleanInput = React.memo<SurveyBooleanInputProps>(
  function SurveyBooleanInput({
    question,
    surveyEngine,
    questionIdToValidationErrorsMap,
    testId,
  }) {
    const selectedValue = useMemo(() => {
      const res = surveyEngine.getResponseForQuestion(
        question.id,
      ) as BooleanResponse;
      return res?.responseValue;
    }, [surveyEngine, question.id]);

    const [touched, setTouched] = useState(true);

    const { errors, showError } = useMemo(() => {
      const errors = questionIdToValidationErrorsMap.get(question.id) ?? [];
      const showError = touched && errors.length > 0;
      return { errors, showError };
    }, [touched, question.id, questionIdToValidationErrorsMap]);

    const onChange = useCallback(
      (value: boolean) => {
        setTouched(true);
        surveyEngine.recordResponse({
          question_id: question.id,
          responseValue: value,
          type: "BOOLEAN",
        });
      },
      [surveyEngine, question.id],
    );

    const onBlur = useCallback(() => {}, []);

    return (
      <Container>
        <Stack data-testid={testId}>
          {[true, false].map((opt) => (
            <OptionButton
              key={`option-button-${opt}`}
              value={opt}
              labelTranslationKey={
                opt
                  ? question.ui.true_translation_key
                  : question.ui.false_translation_key
              }
              surveyEngine={surveyEngine}
              onChange={() => onChange(opt)}
              onBlur={onBlur}
              isValueSelected={selectedValue === opt}
            />
          ))}
        </Stack>
        {/* Mantine Stack component doesn't have built in error state, so made own */}
        {showError ? (
          <Text mt="xl" color="red">
            {errors.join(", ")}
          </Text>
        ) : null}
      </Container>
    );
  },
);
